import React, { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Download, Trash2, Search, ScanText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} Ko`;
  return `${(kb / 1024).toFixed(2)} Mo`;
}

function OcrBadge({ status }) {
  if (status === "done") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">OCR ✓</Badge>;
  if (status === "pending") return <Badge className="bg-amber-100 text-amber-800 border-amber-200 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> OCR…</Badge>;
  if (status === "empty") return <Badge variant="outline" className="text-slate-500">OCR vide</Badge>;
  return null;
}

export default function GED() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ocrDoc, setOcrDoc] = useState(null);
  const [ocrData, setOcrData] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/documents", { params: q ? { q } : {} });
    setRows(data);
  };
  useEffect(() => { load(); }, [q]);

  // Poll pending OCR every 5s
  useEffect(() => {
    const anyPending = rows.some((r) => r.ocr_status === "pending");
    if (!anyPending) return;
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [rows]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploadé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const download = async (id, filename) => {
    try {
      const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur téléchargement");
    }
  };

  const openOcr = async (doc) => {
    setOcrDoc(doc);
    setOcrLoading(true);
    setOcrData(null);
    try {
      const { data } = await api.get(`/documents/${doc.id}/ocr`);
      setOcrData(data);
    } catch (err) {
      toast.error("Impossible de récupérer l'OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  const retryOcr = async (id) => {
    try {
      await api.post(`/documents/${id}/ocr/retry`);
      toast.success("Relance de l'OCR en cours…");
      setTimeout(load, 1500);
    } catch (err) {
      toast.error("Erreur relance OCR");
    }
  };

  const filtered = rows; // server-side search via q

  return (
    <div>
      <PageHeader
        eyebrow="Module"
        title="GED — Documents"
        description="Stockage centralisé + recherche plein texte OCR (français & anglais) sur PDF et images."
        testid="ged-header"
        actions={
          <>
            <input
              ref={inputRef}
              type="file"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
              data-testid="ged-file-input"
            />
            <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-blue-900 hover:bg-blue-800 rounded-full" data-testid="ged-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> {uploading ? "Envoi..." : "Uploader"}
            </Button>
          </>
        }
      />

      <Card className="border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <CardContent className="p-4">
          <div className="mb-4 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Rechercher dans le nom ou le contenu OCR…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ged-search-input"
            />
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fichier</TableHead>
                  <TableHead>OCR</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Taille</TableHead>
                  <TableHead>Ajouté par</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucun document</TableCell></TableRow>
                ) : filtered.map((d) => (
                  <TableRow key={d.id} data-testid={`ged-row-${d.id}`}>
                    <TableCell className="font-medium text-slate-900 dark:text-slate-100">
                      <span className="inline-flex items-center gap-2"><FileText className="w-4 h-4 text-blue-700" /> {d.filename}</span>
                      {d.ocr_text && q && (
                        <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">
                          … {d.ocr_text.slice(0, 220)} …
                        </div>
                      )}
                    </TableCell>
                    <TableCell><OcrBadge status={d.ocr_status} /></TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400 text-xs font-mono">{d.content_type}</TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300 text-sm">{formatSize(d.size)}</TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300 text-sm">{d.uploaded_by_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(d.created_at).toLocaleString("fr-FR")}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {d.ocr_status === "done" && (
                        <Button variant="ghost" size="sm" onClick={() => openOcr(d)} data-testid={`ged-ocr-${d.id}`} title="Voir le texte OCR">
                          <ScanText className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                      {(d.ocr_status === "empty" || d.ocr_status === "pending") && (
                        <Button variant="ghost" size="sm" onClick={() => retryOcr(d.id)} data-testid={`ged-ocr-retry-${d.id}`} title="Relancer l'OCR">
                          <RefreshCw className="w-4 h-4 text-amber-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => download(d.id, d.filename)} data-testid={`ged-download-${d.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(d.id)} data-testid={`ged-delete-${d.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!ocrDoc} onOpenChange={(v) => { if (!v) { setOcrDoc(null); setOcrData(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanText className="w-5 h-5 text-emerald-600" />
              Texte extrait — {ocrDoc?.filename}
            </DialogTitle>
          </DialogHeader>
          {ocrLoading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
          ) : ocrData?.ocr_text ? (
            <div className="max-h-[70vh] overflow-auto whitespace-pre-wrap font-mono text-xs bg-slate-50 dark:bg-slate-950 dark:text-slate-200 p-4 rounded-lg border border-slate-200 dark:border-slate-800" data-testid="ged-ocr-text">
              {ocrData.ocr_text}
            </div>
          ) : (
            <div className="text-slate-500 text-sm">Aucun texte détecté.</div>
          )}
          {ocrData?.ocr_lang && (
            <div className="text-[11px] text-slate-400">Langue : {ocrData.ocr_lang}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
